// Resident Announcements Page - Read-only view
import React, { useState, useMemo } from "react";
import { CalendarDays, Pin, AlertCircle, Info as InfoIcon } from "lucide-react";
import { useActiveAnnouncements, useAnnouncement } from "@/api/announcements";
import type { Announcement, AnnouncementPriority } from "@/types/announcement";
import { useLanguage } from "@/contexts/LanguageContext";

const QUIET =
  "!bg-white !text-gray-900 !border-black/10 border ring-1 ring-black/5 shadow-sm rounded-xl hover:!bg-gray-50 active:!bg-gray-50";

export default function AnnouncementsPage() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [priorityFilter, setPriorityFilter] = useState<"ALL" | AnnouncementPriority>("ALL");
  const { t } = useLanguage();

  const { data: announcements, isLoading } = useActiveAnnouncements();

  const filteredAnnouncements = useMemo(() => {
    if (!announcements) return [];
    let list = [...announcements];
    if (priorityFilter !== "ALL") {
      list = list.filter((a) => a.priority === priorityFilter);
    }
    // Sort: pinned first, then by published date (newest first)
    list.sort((a, b) => {
      if (a.is_pinned !== b.is_pinned) return a.is_pinned ? -1 : 1;
      return +new Date(b.published_at) - +new Date(a.published_at);
    });
    return list;
  }, [announcements, priorityFilter]);

  function getPriorityColor(priority: AnnouncementPriority) {
    switch (priority) {
      case "urgent":
        return "border-red-600 bg-red-50 text-red-900";
      case "high":
        return "border-orange-500 bg-orange-50 text-orange-900";
      case "medium":
        return "border-blue-500 bg-blue-50 text-blue-900";
      case "low":
      default:
        return "border-green-500 bg-green-50 text-green-900";
    }
  }

  function getPriorityIcon(priority: AnnouncementPriority) {
    switch (priority) {
      case "urgent":
      case "high":
        return <AlertCircle className="h-5 w-5" />;
      case "medium":
      case "low":
      default:
        return <InfoIcon className="h-5 w-5" />;
    }
  }

  return (
    <div className="relative space-y-6 sm:space-y-7">
      {/* Ambient background */}
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(1200px_600px_at_50%_-10%,rgba(31,58,147,0.06),transparent_60%)]" />

      {/* Header */}
      <div className="rounded-2xl bg-white/80 backdrop-blur ring-1 ring-black/5 p-4 sm:p-5">
        <h2 className="text-xl font-semibold text-gray-900">{t("announcements.activeTitle")}</h2>
        <p className="text-sm text-gray-600 mt-1">
          {isLoading
            ? t("common.loading")
            : t("announcements.count", { count: filteredAnnouncements.length })}
        </p>
      </div>

      {/* Filter */}
      <div className="rounded-2xl bg-white/80 backdrop-blur ring-1 ring-black/5 p-4 sm:p-5">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t("announcements.filterPriority")}
        </label>
        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value as any)}
          className="w-full sm:w-auto rounded-xl border border-black/10 bg-white px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-black/10"
        >
          <option value="ALL">{t("announcements.filterAll")}</option>
          <option value="urgent">{t("announcements.urgent")}</option>
          <option value="high">{t("issues.high")}</option>
          <option value="medium">{t("issues.medium")}</option>
          <option value="low">{t("issues.low")}</option>
        </select>
      </div>

      {/* List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="rounded-3xl bg-white/90 backdrop-blur ring-1 ring-black/5 p-6 text-center text-gray-600">
            {t("announcements.loading")}
          </div>
        ) : filteredAnnouncements.length > 0 ? (
          filteredAnnouncements.map((a) => (
            <button
              key={a.id}
              onClick={() => setSelectedId(a.id)}
              className="w-full text-left"
            >
              <div
                className={`relative rounded-3xl bg-white/90 backdrop-blur ring-1 ring-black/5 p-5 sm:p-6 shadow-lg shadow-black/10 hover:shadow-xl transition-shadow border-l-4 ${getPriorityColor(
                  a.priority
                )}`}
              >
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className="rounded-2xl p-2.5 bg-white ring-1 ring-black/10 shadow-sm">
                    {getPriorityIcon(a.priority)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{a.title}</h3>
                      {a.is_pinned && (
                        <span title={t("announcements.pinned")} className="text-amber-600">
                          <Pin className="h-4 w-4" />
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white ring-1 ring-black/10 text-xs font-medium">
                        {t(`announcements.priority.${a.priority}`)}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <CalendarDays className="h-4 w-4" />
                        {new Date(a.published_at).toLocaleDateString()}
                      </span>
                      <span>• {t("announcements.postedBy", { name: a.author_name })}</span>
                      {a.expires_at && (
                        <span className="text-gray-500">
                          • {t("announcements.expiresOn")} {new Date(a.expires_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </button>
          ))
        ) : (
          <div className="rounded-3xl bg-white/90 backdrop-blur ring-1 ring-black/5 p-6 text-center text-gray-600">
            {t("announcements.noneMatch")}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedId && (
        <AnnouncementDetailModal
          announcementId={selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}

// Detail Modal Component
function AnnouncementDetailModal({
  announcementId,
  onClose,
}: {
  announcementId: string;
  onClose: () => void;
}) {
  const { data: announcement, isLoading } = useAnnouncement(announcementId);
  const { t } = useLanguage();

  if (isLoading || !announcement) {
    return (
      <div className="fixed inset-0 z-50">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        <div className="absolute inset-x-0 bottom-0 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-2xl">
          <div className="m-0 rounded-t-3xl sm:rounded-3xl bg-white/90 backdrop-blur ring-1 ring-black/10 p-6 shadow-2xl">
            <p className="text-center text-gray-600">
              {isLoading ? t("common.loading") : t("common.noResults")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-0 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-2xl">
        <div className="m-0 rounded-t-3xl sm:rounded-3xl bg-white/90 backdrop-blur ring-1 ring-black/10 p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold text-gray-900">{announcement.title}</h2>
              {announcement.is_pinned && (
                <span title={t("announcements.pinned")} className="text-amber-600">
                  <Pin className="h-5 w-5" />
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-sm"
            >
              {t("common.close")}
            </button>
          </div>

          {/* Metadata */}
          <div className="rounded-2xl ring-1 ring-black/5 bg-white/70 p-4 mb-4">
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">{t("announcements.priorityLabel")}</span>
                <span className="font-medium text-gray-900">
                  {t(`announcements.priority.${announcement.priority}`)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t("announcements.published")}</span>
                <span className="font-medium text-gray-900">
                  {new Date(announcement.published_at).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t("announcements.author")}</span>
                <span className="font-medium text-gray-900">{announcement.author.full_name}</span>
              </div>
              {announcement.expires_at && (
                <div className="flex justify-between">
                  <span className="text-gray-500">{t("announcements.expiresOn")}</span>
                  <span className="font-medium text-gray-900">
                    {new Date(announcement.expires_at).toLocaleDateString()}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">{t("common.building")}</span>
                <span className="font-medium text-gray-900">{announcement.building.name}</span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="mb-6">
            <div className="text-xs text-gray-500 mb-2">{t("announcements.message")}</div>
            <p className="text-sm text-gray-800 leading-6 whitespace-pre-wrap">
              {announcement.content}
            </p>
          </div>

          {/* Footer */}
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className={`px-4 py-2 text-sm ${QUIET}`}
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
